import ApplicationModel from '../models/application'
import r from 'structure-driver'

export default class UserService {

  /**
   * UserService constructor
   *
   * @public
   * @constructor
   * @param {Object} applicationId - ID of application to add user to
   * @param {Object} userId - ID of user to add to application
   * @param {Object} logger - logger to use
   */
  constructor(applicationId, userId, logger) {
    this.applicationId = applicationId
    this.userId = userId
    this.logger = logger
    this.application = new ApplicationModel({
      logger: logger
    })
  }

  /**
   * Add a user to a application
   *
   * @public
   * @param {Array.String} roles - array of roles user has in application
   * @returns {Promise.Boolean} Promise for whether the user was actually
   *   added or not
   */
  addUser(roles) {

    return new Promise( async (resolve, reject) => {

      try {
        const hasReference = await r
          .table('link_applications_users')
          .filter({
            applicationId: this.applicationId,
            userId: this.userId
          })
          .limit(1)

        if(hasReference && hasReference.length > 0) {

          this.logger.debug(
            'User already added to application',
            {
              applicationId: this.applicationId,
              userId: this.userId
            }
          )

          await this.updateUser(roles)

          resolve(false)

        } else {

          await this.application.createReference({
            applicationId: this.applicationId,
            userId: this.userId,
            roles
          })

          this.logger.debug(
            'User added to application',
            {
              applicationId: this.applicationId,
              userId: this.userId
            }
          )

          resolve(true)
        }

      } catch(e) {

        this.logger.error('Error adding user to an application', e)
        reject(e)

      }

    })
  }

  /**
   * Update a user on an application
   *
   * @public
   * @param {Array.String} roles - array of roles user has in application
   * @returns {Promise.Boolean} Promise for whether the user was actually
   *   updated or not
   */
  updateUser(roles) {

    return new Promise( async (resolve, reject) => {

      try {
        const hasReference = await r
          .table('link_applications_users')
          .filter({
            applicationId: this.applicationId,
            userId: this.userId
          })
          .limit(1)

        if(hasReference && hasReference.length > 0) {

          await r
            .table('link_applications_users')
            .filter({
              applicationId: this.applicationId,
              userId: this.userId
            })
            .update({
              roles
            })

          this.logger.debug(
            'User updated on application',
            {
              applicationId: this.applicationId,
              userId: this.userId
            }
          )

          resolve(true)

        } else {

          this.logger.debug(
            "User doesn't exist, can't update on application",
            {
              applicationId: this.applicationId,
              userId: this.userId
            }
          )

          resolve(false)

        }

      } catch(e) {

        this.logger.error('Error updated user on application', e)
        reject(e)

      }

    })
  }

  /**
   * Remove a user from a application
   *
   * @public
   * @returns {Promise.Boolean} Promise for whether the user was actually
   *   removed or not
   */
  removeUser() {
    return new Promise( async (resolve, reject) => {

      try {
        const hasReference = await r
          .table('link_applications_users')
          .filter({
            applicationId: this.applicationId,
            userId: this.userId
          })
          .limit(1)

        if(hasReference.length === 0) {
          this.logger.debug(
            'User not in application, no need to remove',
            {
              applicationId: this.applicationId,
              userId: this.userId
            }
          )

          resolve(false)
        } else {
          await this.application.deleteReference({
            applicationId: this.applicationId,
            userId: this.userId
          })

          this.logger.debug(
            'User removed from application',
            {
              applicationId: this.applicationId,
              userId: this.userId
            }
          )

          resolve(true)
        }

      } catch(e) {

        reject(e)

      }
    })
  }
}
